
import { Component, Vue, Prop } from 'vue-property-decorator'
import { Permission, PermissionRoleFilters, PermissionUpdate, Role, RoleCatalogFilters, RoleCatalogGridItem } from '@/types/Catalogs'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import ItemsTable from '@/components/generic/ItemsTable.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import catalogsService from '@/services/CatalogsService'
import { PermissionNames } from '@/types/Auth'
import userInfoStore from '@/store/userInfo'
import { PageRequest } from '@/types/Support'
import moment from 'moment'
import DateFormat from '@/services/DateFormat'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    LoadingMask,
    ItemsTable,
    MessageError
  }
})
export default class AdminPermissionForm extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: true, default: '' })
  title: string

  @Prop({ required: false, default: false })
  readOnly: boolean

  @Prop({ required: false, default: 0 })
  domainId: number

  loading: boolean = false
  loadingRoles: boolean = false

  form = Permission.getIntanceForEdit()
  formUpdated = PermissionUpdate.getIntanceForEdit()
  versionOptions = [
    { text: 'Versión 1', value: 1 },
    { text: 'Versión 2', value: 2 }
  ]
  domainOptions: any[] = []

  PAGE_SIZE = 500
  backupPermission = ''
  /** To work with role sections */
  filterRoles = new PermissionRoleFilters()
  allRoles: RoleCatalogGridItem[] = []
  creationDate = ''
  updateDate = ''

  mounted () {
    if (this.id) {
      this.loading = true
      catalogsService.getPermissionsById(this.id).then(data => {
        this.form = data
        this.filterRoles.roleIds = data.roles.map((role) => { return role.id })
        this.filterRoles.totalRoles = data.roles.length
        this.creationDate = data.creationDate ? new DateFormat(data.creationDate).toDateTimeString() : '- - / - - / - - - -'
        this.updateDate = data.updateDate ? new DateFormat(data.updateDate).toDateTimeString() : '- - / - - / - - - -'
        if (this.form.domain !== -1) {
          catalogsService.getDomainById(this.form.domain).then((domain) => {
            this.domainOptions.push({ value: domain.id, text: domain.name })
          })
        }
      }).finally(() => {
        this.sincronizeFormUpdated(this.form, this.form.roles.map((role) => { return role.id }))
        this.backupPermission = JSON.stringify(this.formUpdated)
        this.loading = false
      })
    }
    this.domainOptions.push({ value: this.form.domain, text: 'GLOBAL' })
  }

  /** Permissions */

  /**
   * Checks if user has 'permissions:read'
   */
  get readPermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.PERMISSIONS_READ)
  }

  /**
   * Checks if user has 'permissions:update'
   */
  get updatePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.PERMISSIONS_UPDATE)
  }

  /**
   * Checks if user has 'permissions:delete'
   */
  get deletePermission () {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === PermissionNames.PERMISSIONS_DELETE)
  }

  /** General actions */

  /**
   * Sincronizes 'formUpdated' with 'form' and assign 'rolesIds'
   * @param form - current permission being modified
   * @param roleIds - current role ids
   */
  sincronizeFormUpdated (form: Permission, roleIds: number[]) {
    this.formUpdated.id = form.id
    this.formUpdated.name = form.name
    this.formUpdated.domain = form.domain
    this.formUpdated.resource = form.resource
    this.formUpdated.effect = form.effect
    this.formUpdated.label = form.label
    this.formUpdated.version = form.version
    this.formUpdated.description = form.description
    this.formUpdated.roles = roleIds
    this.formUpdated.creationDate = form.creationDate
    this.formUpdated.creationUser = form.creationUser
    this.formUpdated.updateUser = form.updateUser
  }

  /**
   * Submits form values to save changes with a PUT/POST request
   */
  onSubmit () {
    this.sincronizeFormUpdated(this.form, this.filterRoles.roleIds)
    this.$validator.validateAll().then(result => {
      if (result && this.validateVersion()) {
        if (JSON.stringify(this.formUpdated) === this.backupPermission) {
          // If there are no changes then return to main grid
          this.onCancel()
          return
        }
        this.formUpdated.updateDate = new Date()
        if (this.id) {
          let msgConfirm = `Has realizado cambios en el registro: ${this.form.id} - ${this.form.name}. ¿Estás seguro de guardar los cambios?`
          this.$bvModal.msgBoxConfirm(msgConfirm, {
            title: 'Confirmar acción',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: 'Confirmar',
            cancelVariant: 'outline-secondary',
            cancelTitle: 'Cancelar',
            headerClass: 'p-2 border-bottom-0',
            footerClass: 'p-2 border-top-0',
            hideHeaderClose: false
          }).then((value) => {
            if (value) {
              this.savePermission()
            }
          })
        } else {
          this.savePermission()
        }
      } else {
        this.$bvToast.toast('Revise los campos marcados con error', {
          title: 'Errores en el formulario',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 4500
        })
      }
    })
  }

  /**
   * POST/PUT request to save the form values
   */
  savePermission () {
    this.loading = true
    this.formUpdated.updateDate = new Date()
    catalogsService.savePermission(this.formUpdated).then((response: PermissionUpdate) => {
      this.formUpdated.id = response.id
      this.form.id = response.id
      this.$emit('saved', this.form)
    }).catch((error) => {
      let messageValues = {
        title: 'Error al guardar el permiso',
        message: '',
        delay: 10000,
        error: error
      }
      let errorComponent = new MessageError({ propsData: messageValues })
      errorComponent.displayMessage()
    }).finally(() => {
      this.loading = false
    })
  }

  /**
   * Switch to edit mode
   */
  changeEditMode () {
    this.$emit('edit-mode')
  }

  /**
   * Close form view
   */
  onCancel () {
    this.$emit('close')
  }

  /**
   * Generates automatically the name for permission
   */
  createName () {
    this.form.name = this.form.resource + ':' + this.form.effect
  }

  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  /**
   * Cheks if version has been selected
   */
  validateVersion () {
    if (this.form.version <= 0) {
      return false
    }
    return true
  }

  /**
   * Get all roles available and assign it in 'allRoles'
   */
  loadRoles (pageRequest: PageRequest<any>) {
    this.loadingRoles = true
    catalogsService.getRoles(pageRequest).then(response => {
      this.filterRoles.allRoles.push(...response.results.map((role: Role): RoleCatalogGridItem => {
        return this.generateRoleGrid(role, new RoleCatalogGridItem())
      }))
      if (this.filterRoles.allRoles.length < response.total) {
        pageRequest.page++
        this.loadRoles(pageRequest)
      }
    }).catch((error) => {
      LogService.error(`Error en la carga de roles: ${ToString(error.response)}`)
      this.loadingRoles = false
    }).finally(() => {
      this.loadingRoles = false
    })
  }

  /**
   * Update target role form origin role
   * @param origin - role entity to get values
   * @param target - roleCatalogGridItem to update
   * @returns a RoleCatalogGridItem updated
   */
  generateRoleGrid (origin: Role, target: RoleCatalogGridItem) {
    target.id = origin.id
    target.name = origin.name
    target.copyFromRole = origin.copyFromRole
    target.maxSessions = origin.maxSessions
    target.domain = origin.domain
    target.permissions = origin.permissions
    target.creation = origin.creationDate ? moment(origin.creationDate).format('DD/MM/YYYY') : '--'
    target.creationUser = origin.creationUser ? origin.creationUser.name : '---'
    target.update = origin.updateDate ? moment(origin.updateDate).format('DD/MM/YYYY') : '--'
    target.updateUser = origin.updateUser ? origin.updateUser.name : '---'
    return target
  }

  /**
   * Updates role ids on emit action from ItemsTable
   * @param newRoles that will add
   */
  updateRoles (newRoles: number[]) {
    this.filterRoles.roleIds = this.filterRoles.roleIds.concat(newRoles)
    this.formUpdated.roles = this.filterRoles.roleIds
    this.formUpdated.roles = this.filterRoles.allRoles.filter(role =>
      this.filterRoles.roleIds
        .includes(role.id))
      .map((role) => { return role.id })
  }

  /**
   * Remove role ids that was selected
   * @param roles that must remove
   */
  deleteRoles (roles: number[]) {
    this.filterRoles.roleIds = this.filterRoles.roleIds.filter(roleId => !roles.includes(roleId))
    this.form.roles = this.form.roles.filter(role => !roles.includes(role.id))
  }

  /**
   * Returns related roles from form
   */
  get relatedRoles () {
    let roles: any[] = []
    roles = roles.concat(this.form.roles)
    if (this.filterRoles.allRoles.length > 0) {
      this.filterRoles.allRoles.forEach(element => {
        if (!roles.find(role => role.id === element.id) && this.filterRoles.roleIds.includes(element.id)) {
          roles.push(element)
        }
      })
    }
    return roles
  }

  /**
   * Call loadRoles function
   */
  callLoadRoles () {
    this.filterRoles.allRoles = []
    let pageRequest = new PageRequest<RoleCatalogFilters>(this.PAGE_SIZE)
    if (userInfoStore.inRootDomain()) {
      pageRequest.filters.domain = this.domainId
    }
    this.loadRoles(pageRequest)
  }

  /**
   * Emit event to remove item
   */
  deleteItem () {
    this.$emit('delete-item')
  }

  /**
   * Updates current Permission to sincronized extern changes
   */
  sincronizeForm () {
    if (this.id) {
      this.loading = true
      catalogsService.getPermissionsById(this.id).then(data => {
        this.form = data
        this.filterRoles.roleIds = data.roles.map((role) => { return role.id })
        this.filterRoles.totalRoles = data.roles.length
      }).finally(() => {
        this.sincronizeFormUpdated(this.form, this.form.roles.map((role) => { return role.id }))
        this.backupPermission = JSON.stringify(this.formUpdated)
        this.loading = false
      })
    }
  }
}
